import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import FillMissing from "../question/FillMissing";
import SingleAnswer from "../question/SingleAnswer";
import AnswerWithText from "../question/AnswerWithText";
import MultipleAnswers from "../question/MultipleAnswers";
import QuestionInstructionDetail from "../question/QuestionInstructionDetail";
import MapRowColumn from '../question/MapRowColumn';
import FillMissingInTable from "../question/FillMissingInTable";
import FillMissingByDragDrop from "../question/FillMissingByDragDrop";
import { Check } from "lucide-react"; // Import the tick icon

const ListeningComponent = () => {
    const navigate = useNavigate();
    const [examData, setExamData] = useState([]);
    const audioRef = useRef(null);
    const { id } = useParams();
    const [answerMap, setAnswerMap] = useState(new Map());

    useEffect(() => {
        if (!id) return;
        fetch(`/exams/${id}/Exam.json`)
            .then((response) => response.json())
            .then((data) => {
                if (Array.isArray(data.listening)) {
                    setExamData(data.listening); // ✅ Ensure it's an array
                } else {
                    console.error("Error: `listening` is not an array", data.listening);
                }
            })
            .catch((error) => console.error("Error loading exam data:", error));
    }, [id]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Convert Map to an object before passing it to navigate
        const answerObject = Object.fromEntries(answerMap);

        navigate("/submit-result", { state: { answers: answerObject } });
    };

    const handleCalculateScore = (type, questionNumber, userAnswers, correctAnswers) => {
        console.log(questionNumber, userAnswers, correctAnswers)
        if (type === 2) {
            if (userAnswers.length === correctAnswers.length) {
                setAnswerMap(prevMap => {
                    const newMap = new Map(prevMap);
                    newMap.set(questionNumber, { userAnswers, correctAnswers }); // Update existing or add new
                    return newMap;
                });
            } else {
                setAnswerMap(prevMap => {
                    const newMap = new Map(prevMap);
                    newMap.delete(questionNumber); // Update existing or add new
                    return newMap;
                });
            }
        } else if (type === 4) {
            if (userAnswers.length === correctAnswers.length) {
                setAnswerMap(prevMap => {
                    const newMap = new Map(prevMap);
                    newMap.set(questionNumber, { userAnswers, correctAnswers }); // Update existing or add new
                    return newMap;
                });
            } else {
                setAnswerMap(prevMap => {
                    const newMap = new Map(prevMap);
                    newMap.delete(questionNumber); // Update existing or add new
                    return newMap;
                });
            }
        } else {
            setAnswerMap(prevMap => {
                const newMap = new Map(prevMap);
                newMap.set(questionNumber, { userAnswers, correctAnswers }); // Update existing or add new
                return newMap;
            });
        }
        console.log(questionNumber, userAnswers, correctAnswers)
    };

    return (
        <div className="w-full h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="w-full h-[50px] bg-white flex items-center px-4 border-b border-gray-300 justify-between">
                {/* Logo Section */}
                <div className="flex items-center justify-center h-full bg-gray-200 rounded-md px-4">
                    <Link to={"/student-dashboard"}><span className="text-sm text-gray-700">IELTS Virtual Exam</span></Link>
                </div>

                {/* Centered Text */}
                <div className="flex-1 flex justify-center">
                    <p><b>Listening</b></p>
                </div>

                {/* Navigation Links */}
                <div className="flex space-x-4">
                    <Link to={`/reading/${id}`} className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Reading</Link>
                    <Link to={`/writing/${id}`} className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600">Writing</Link>
                </div>
            </div>

            {/* Main Content */}
            <div className="w-full flex-1 flex flex-col justify-center items-center">
                <div className="w-full bg-white p-5">
                    {examData.length > 0 ? (
                        <div className="h-[80vh] overflow-y-auto">
                            {examData.map((part, partIndex) => (
                                <div key={partIndex} className="mb-6">
                                    {/* Part Header */}
                                    <div className="w-full bg-gray-200 rounded-lg my-4 p-4">
                                        {/* Part Number and Question */}
                                        <div className="text-lg font-semibold text-gray-800">
                                            Part {part.part}
                                        </div>
                                        {/* Instruction */}
                                        <div className="text-sm font-medium mt-1 whitespace-pre-line">
                                            {part.instruction}
                                        </div>

                                        {/* Conditionally Render Image */}
                                        {part.image && (
                                            <div className="mt-3 flex justify-center">
                                                <img
                                                    src={part.image}
                                                    alt="Question"
                                                    className="max-w-full h-auto rounded-lg shadow-md"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Questions */}
                                    <div>
                                        {part.questions.map((question, questionIndex) => {
                                            if (question.type === 1) {
                                                return (
                                                    <FillMissing
                                                        key={questionIndex}
                                                        questionNumber={question.questionNumber}
                                                        questionText={question.question_text}
                                                        instruction={question.instruction}
                                                        paragraph={question.answers.join("\n")}
                                                        correctAnswers={question.correct_answers} // Pass correct answers
                                                        onCalculateScore={handleCalculateScore}
                                                    />
                                                );
                                            } else if (question.type === 3) {
                                                return (
                                                    <SingleAnswer
                                                        key={questionIndex}
                                                        questionNumber={question.questionNumber}
                                                        question={question.question}
                                                        answers={question.answers}
                                                        correctAnswer={question.correct_answer}
                                                        onCalculateScore={handleCalculateScore}
                                                    />
                                                );
                                            } else if (question.type === 0) {
                                                return (
                                                    <AnswerWithText
                                                        key={questionIndex}
                                                        questionNumber={question.questionNumber}
                                                        question={question.question}
                                                        correctAnswer={question.correctAnswer}
                                                        onCalculateScore={handleCalculateScore}
                                                    />
                                                );
                                            } else if (question.type === 2) {
                                                return (
                                                    <MultipleAnswers
                                                        key={questionIndex}
                                                        questionNumber={question.questionNumber}
                                                        question={question.question}
                                                        answers={question.answers}
                                                        correctAnswer={question.correctAnswer}
                                                        onCalculateScore={handleCalculateScore}
                                                    />
                                                );
                                            } else if (question.type === 6) {
                                                return (
                                                    <QuestionInstructionDetail
                                                        instruction={question.instruction}
                                                    />
                                                );
                                            } else if (question.type === 4) {
                                                return (<MapRowColumn
                                                    key={questionIndex}
                                                    questionNumber={question.questionNumber}
                                                    image={question.image}
                                                    rows={question.rows}
                                                    columns={question.columns}
                                                    correctAnswer={question.correctAnswer}
                                                    onCalculateScore={handleCalculateScore}
                                                />);
                                            } else if (question.type === 7) {
                                                return (
                                                    <FillMissingInTable
                                                        key={questionIndex}
                                                        questionNumber={question.questionNumber}
                                                        answers={question.answers}
                                                        correctAnswers={question.correct_answers} // Pass correct answers
                                                        onCalculateScore={handleCalculateScore}
                                                    />
                                                );
                                            } else if (question.type === 8) {
                                                return (<FillMissingByDragDrop
                                                    key={questionIndex}
                                                    questionNumber={question.questionNumber}
                                                    answers={question.answers}
                                                    words={question.words}
                                                    correctAnswers={question.correct_answers} // Pass correct answers
                                                    onCalculateScore={handleCalculateScore}
                                                />);
                                            }
                                            return null; // 🚨 Must return null if condition is false to avoid errors
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>Loading...</p>
                    )}
                    {/* Sidebar (Fixed at Bottom-Left) */}
                    <div className="fixed bottom-0 left-0 w-1/4 p-10">
                        <div className="mt-2 flex space-x-4">
                            {examData.map((part, partIndex) => {
                                // Check if all questions in the part are answered
                                const allAnswered = part.questions.every(q => q.type === 6 || answerMap.has(q.questionNumber));
                                return (
                                    <div key={partIndex} className="flex items-end space-x-4">
                                        {/* Part Label with Line Above */}
                                        <div className="flex flex-col items-center">
                                            <div className={`h-1 w-full mb-1 ${allAnswered ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                            <p className="text-sm font-semibold whitespace-nowrap">Part {part.part}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* Submit Button (Fixed at Bottom-Right) */}
                    <div className="fixed bottom-0 right-0 p-4">
                        <button
                            className="w-20 h-12 bg-gray-300 text-black rounded-md flex items-center justify-center cursor-pointer hover:bg-gray-500 transition"
                            onClick={handleSubmit}
                        >
                            <Check className="w-6 h-6 text-black" />
                        </button>
                    </div>

                </div>
            </div>
            {/* Hidden Audio Element */}
            <audio ref={audioRef} />
        </div>
    );
};

export default ListeningComponent;
